import { ReactNode } from "react";
import { cn } from "../../lib/util";

/**
 * Shared visual language for booking inputs. Soft rose hairline borders, a
 * generous 48px-tall touch target, and a rose focus ring that matches the
 * site's accent — deliberately warmer than a default form control.
 */
export const fieldControlClass =
  "w-full rounded-xl border border-[#E3C9C8] bg-white px-4 py-3 text-base text-[#3a2e2c] " +
  "placeholder:text-[#bcaca9] transition-colors outline-none " +
  "focus-visible:ring-2 focus-visible:ring-[#B87775]/50 focus-visible:border-[#B87775]";

export const fieldErrorClass =
  "border-[#d98c89] focus-visible:ring-[#d98c89]/50 focus-visible:border-[#c46f6c]";

interface ChildProps {
  id: string;
  "aria-invalid": boolean | undefined;
  "aria-describedby": string | undefined;
  "aria-required": boolean | undefined;
}

interface FormFieldProps {
  id: string;
  label: ReactNode;
  /** Marks the field with a rose asterisk and sets aria-required. */
  required?: boolean;
  /** Renders a muted "(optional)" tag instead of an asterisk. */
  optional?: boolean;
  /** Quiet helper copy shown beneath the label. */
  hint?: ReactNode;
  /** Error message; presence flips the control into its invalid styling. */
  error?: string;
  className?: string;
  /**
   * Render-prop so the control receives the right aria wiring (id, invalid,
   * describedby) regardless of whether it's an <input>, Radix Select, etc.
   */
  children: (props: ChildProps) => ReactNode;
}

export function FormField({
  id,
  label,
  required,
  optional,
  hint,
  error,
  className,
  children,
}: FormFieldProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("space-y-2", className)}>
      <label
        htmlFor={id}
        className="block font-serif text-[17px] leading-snug text-[#3a2e2c]"
      >
        {label}
        {required && (
          <span className="text-[#B87775]" aria-hidden="true">
            {" "}
            *
          </span>
        )}
        {optional && (
          <span className="ml-1.5 align-middle text-sm italic text-[#9A6B69]/80">
            optional
          </span>
        )}
      </label>

      {hint && (
        <p id={hintId} className="text-sm leading-relaxed text-[#8a7a78]">
          {hint}
        </p>
      )}

      {children({
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy,
        "aria-required": required || undefined,
      })}

      {/* aria-live so screen readers announce validation as it resolves. */}
      <div aria-live="polite">
        {error && (
          <p
            id={errorId}
            className="flex items-start gap-1.5 text-sm text-[#c0504d]"
          >
            <span aria-hidden="true" className="mt-px select-none">
              ✕
            </span>
            <span>{error}</span>
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Reusable native-radio selectors, shared across the booking & quote flows ── */

const pillClass =
  "flex min-h-[44px] min-w-[3rem] cursor-pointer items-center justify-center rounded-full " +
  "border border-[#E3C9C8] bg-white px-5 text-base text-[#3a2e2c] transition " +
  "peer-checked:border-[#B87775] peer-checked:bg-[#B87775] peer-checked:text-white " +
  "peer-focus-visible:ring-2 peer-focus-visible:ring-[#B87775] peer-focus-visible:ring-offset-2 " +
  "hover:border-[#B87775]";

export function RadioPill({
  name,
  value,
  checked,
  onChange,
  children,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
}) {
  const id = `${name}-${value}`;
  return (
    <label htmlFor={id} className="relative">
      <input
        type="radio"
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="peer sr-only"
      />
      <span className={pillClass}>{children}</span>
    </label>
  );
}

export function RadioCard({
  name,
  value,
  checked,
  onChange,
  title,
  blurb,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  title: ReactNode;
  blurb: ReactNode;
}) {
  const id = `${name}-${value}`;
  return (
    <label htmlFor={id} className="relative block">
      <input
        type="radio"
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="peer sr-only"
      />
      <span
        className={cn(
          "flex h-full cursor-pointer flex-col gap-1 rounded-xl border border-[#E3C9C8] bg-white px-4 py-4 transition",
          "hover:border-[#B87775]/70",
          "peer-checked:border-[#B87775] peer-checked:bg-[#FBEFEF] peer-checked:ring-1 peer-checked:ring-[#B87775]",
          "peer-focus-visible:ring-2 peer-focus-visible:ring-[#B87775] peer-focus-visible:ring-offset-2"
        )}
      >
        <span className="font-serif text-lg text-[#3a2e2c]">{title}</span>
        <span className="text-sm text-[#7c6c6a]">{blurb}</span>
      </span>
    </label>
  );
}
